import React, { useState } from 'react';
import { Calendar, ChevronDown } from 'lucide-react';
import { useGA4 } from '../../contexts/GA4Context';
import { getDateRangePreset } from '../../utils/analytics-helpers';
import './TimeRangeSelector.css';

/**
 * TimeRangeSelector - Control date range and comparison settings
 */
const TimeRangeSelector = () => {
  const {
    dateRange,
    comparisonEnabled,
    comparisonType,
    updateDateRange,
    toggleComparison,
    updateComparisonType
  } = useGA4();

  const [showDropdown, setShowDropdown] = useState(false);
  const [activePreset, setActivePreset] = useState('30d');

  const presets = [
    { label: 'Today', value: 'today' },
    { label: 'Yesterday', value: 'yesterday' },
    { label: 'Last 7 days', value: '7d' },
    { label: 'Last 30 days', value: '30d' },
    { label: 'This month', value: 'thisMonth' },
    { label: 'Last month', value: 'lastMonth' },
    { label: 'Last 90 days', value: '90d' },
    { label: 'This year', value: 'thisYear' }
  ];

  const handlePresetSelect = (preset) => {
    setActivePreset(preset.value);

    // Convert to GA4 format
    let startDate, endDate;
    const now = new Date();

    switch(preset.value) {
      case 'today':
        startDate = 'today';
        endDate = 'today';
        break;
      case 'yesterday':
        startDate = 'yesterday';
        endDate = 'yesterday';
        break;
      case '7d':
        startDate = '7daysAgo';
        endDate = 'today';
        break;
      case '30d':
        startDate = '30daysAgo';
        endDate = 'today';
        break;
      case 'thisMonth':
        // First day of current month
        const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
        startDate = firstDay.toISOString().split('T')[0];
        endDate = 'today';
        break;
      case 'lastMonth':
        // First day of last month
        const lastMonthFirst = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        // Last day of last month
        const lastMonthLast = new Date(now.getFullYear(), now.getMonth(), 0);
        startDate = lastMonthFirst.toISOString().split('T')[0];
        endDate = lastMonthLast.toISOString().split('T')[0];
        break;
      case '90d':
        startDate = '90daysAgo';
        endDate = 'today';
        break;
      case 'thisYear':
        // First day of current year
        const yearStart = new Date(now.getFullYear(), 0, 1);
        startDate = yearStart.toISOString().split('T')[0];
        endDate = 'today';
        break;
      default:
        startDate = '30daysAgo';
        endDate = 'today';
    }

    updateDateRange({ startDate, endDate });
    setShowDropdown(false);
  };

  const handleComparisonToggle = () => {
    toggleComparison(!comparisonEnabled);
  };

  const handleComparisonTypeChange = (e) => {
    updateComparisonType(e.target.value);
  };

  const getCurrentPresetLabel = () => {
    const preset = presets.find(p => p.value === activePreset);
    return preset ? preset.label : 'Custom range';
  };

  return (
    <div className="time-range-selector">
      <div className="time-range-main">
        <Calendar size={20} />

        <div className="time-range-dropdown">
          <button
            className="time-range-button"
            onClick={() => setShowDropdown(!showDropdown)}
          >
            <span>{getCurrentPresetLabel()}</span>
            <ChevronDown size={16} />
          </button>

          {showDropdown && (
            <div className="time-range-menu">
              {presets.map(preset => (
                <button
                  key={preset.value}
                  className={`time-range-option ${activePreset === preset.value ? 'active' : ''}`}
                  onClick={() => handlePresetSelect(preset)}
                >
                  {preset.label}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="time-range-comparison">
        <label className="comparison-toggle">
          <input
            type="checkbox"
            checked={comparisonEnabled}
            onChange={handleComparisonToggle}
          />
          <span>Compare to previous period</span>
        </label>

        {comparisonEnabled && (
          <select
            className="comparison-type-select"
            value={comparisonType}
            onChange={handleComparisonTypeChange}
          >
            <option value="week">Week over week</option>
            <option value="month">Month over month</option>
          </select>
        )}
      </div>
    </div>
  );
};

export default TimeRangeSelector;
